import type { Event, EventReminder } from 'models';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { CopyLineIcon, Delete2LineIcon, type MenuProps, type ModalOptions, PencilLineIcon, message } from 'ui';
import { DuplicateReminderModal, type DuplicateReminderModalOptions } from '../../../duplicate-reminder-modal';
import { EditReminderModal, type EditReminderModalOptions } from '../../../edit-reminder-modal';
import { useDeleteReminder } from '../../../hooks';

type P = {
  navigateToCalendar?: () => void;
  navigateToEventDetail: (id: string) => Promise<void>;
};

export const useReminderDropdownActions = ({ navigateToCalendar, navigateToEventDetail }: P) => {
  const { t } = useTranslation();
  const { canCancelSocialEvent, canDuplicateSocialEvent, canEditSocialEvent } = usePermissions();
  const [editReminderOptions, setEditReminderOptions] = useState<ModalOptions<EditReminderModalOptions>>({
    isOpened: false,
  });
  const [duplicateReminderOptions, setDuplicateReminderOptions] = useState<ModalOptions<DuplicateReminderModalOptions>>(
    { isOpened: false },
  );

  const onDeleteReminderSuccess = useCallback(() => {
    message.success('Reminder deleted successfully');
    navigateToCalendar?.();
  }, [navigateToCalendar]);

  const { confirm: deleteReminder, contextHolder: contextHolderDelete } = useDeleteReminder({
    onDeleteSuccess: onDeleteReminderSuccess,
  });

  const closeEditReminderModal = useCallback(() => {
    setEditReminderOptions({ isOpened: false });
  }, []);

  const closeDuplicateReminderModal = useCallback(() => {
    setDuplicateReminderOptions({ isOpened: false });
  }, []);

  const onDuplicateReminderSuccess = useCallback(
    async (eventData: Event) => {
      closeDuplicateReminderModal();
      await navigateToEventDetail(eventData.id);
    },
    [closeDuplicateReminderModal, navigateToEventDetail],
  );

  const getActions = useCallback(
    ({ event }: { event: EventReminder }) => {
      const actions: MenuProps['items'] = [];

      if (canEditSocialEvent({ event })) {
        actions.push({
          icon: <PencilLineIcon />,
          key: 'edit',
          label: t('Edit'),
          onClick() {
            setEditReminderOptions({ event, isOpened: true });
          },
        });
      }

      if (canDuplicateSocialEvent({ event })) {
        actions.push({
          icon: <CopyLineIcon />,
          key: 'duplicate',
          label: t('Duplicate'),
          onClick() {
            setDuplicateReminderOptions({ event, isOpened: true });
          },
        });
      }

      if (canCancelSocialEvent({ event })) {
        actions.push({
          danger: true,
          icon: <Delete2LineIcon />,
          key: 'delete',
          label: t('Delete'),
          onClick() {
            deleteReminder({ event });
          },
        });
      }

      return actions;
    },
    [canCancelSocialEvent, canDuplicateSocialEvent, canEditSocialEvent, deleteReminder, t],
  );

  return useMemo(
    () => ({
      contextHolders: (
        <>
          {contextHolderDelete}
          <EditReminderModal
            onCancel={closeEditReminderModal}
            onSuccess={closeEditReminderModal}
            options={editReminderOptions}
          />
          <DuplicateReminderModal
            onCancel={closeDuplicateReminderModal}
            onSuccess={onDuplicateReminderSuccess}
            options={duplicateReminderOptions}
          />
        </>
      ),
      getActions,
    }),
    [
      closeDuplicateReminderModal,
      closeEditReminderModal,
      contextHolderDelete,
      duplicateReminderOptions,
      editReminderOptions,
      getActions,
      onDuplicateReminderSuccess,
    ],
  );
};
